import type { GroundDecalDef } from '@/world-prototype/objects/groundDecal';
import { ROUNDED_CONCRETE_PATIO_PROCEDURAL } from '@/world-prototype/objects/roundedConcretePatio';

/**
 * Spanish Fork backyard ground decals.
 *
 * Patio = freeform tile plate (Hubert art). Circle Patio = soft circular pad
 * under the gazebo. Rounded Concrete Patio = procedural poured concrete with
 * independent width/height and reflowing control joints. Layouts are
 * independent of Hubert and Holly Springs. Updated by World Editor (?edit=1).
 */
const SHARED_GROUND = `${import.meta.env.BASE_URL}art/environments/shared/ground`;
const BACKYARD_GROUND = `${import.meta.env.BASE_URL}art/environments/jacksonville/backyard/ground`;

export const SPANISH_FORK_GROUND_DECAL_DEFS: readonly GroundDecalDef[] = [
  {
    id: 'patio',
    title: 'Patio',
    enabled: true,
    x: 0.42,
    y: 0.9,
    widthFrac: 0.42,
    rotationDeg: -1.2,
    layouts: {
      desktop: {
        x: 0.42,
        y: 0.9,
        widthFrac: 0.42,
        rotationDeg: -1.2,
        quadCorners: [
          { x: -0.18, y: -0.055 },
          { x: 0.2, y: -0.05 },
          { x: 0.22, y: 0.06 },
          { x: -0.19, y: 0.055 },
        ],
      },
      tablet: {
        x: 0.42,
        y: 0.9,
        widthFrac: 0.4,
        rotationDeg: -1.2,
        quadCorners: [
          { x: -0.17, y: -0.052 },
          { x: 0.19, y: -0.048 },
          { x: 0.21, y: 0.058 },
          { x: -0.18, y: 0.052 },
        ],
      },
      mobile: {
        x: 0.42,
        y: 0.9,
        widthFrac: 0.38,
        rotationDeg: -1.2,
        quadCorners: [
          { x: -0.16, y: -0.05 },
          { x: 0.18, y: -0.046 },
          { x: 0.2, y: 0.055 },
          { x: -0.17, y: 0.05 },
        ],
      },
    },
    quadCorners: [
      { x: -0.18, y: -0.055 },
      { x: 0.2, y: -0.05 },
      { x: 0.22, y: 0.06 },
      { x: -0.19, y: 0.055 },
    ],
    sprite: {
      src: `${BACKYARD_GROUND}/patio-v1.png`,
      alt: 'Concrete patio',
      imageWidth: 1013,
      imageHeight: 130,
    },
    footprint: [],
  },
  {
    id: 'circle-patio',
    title: 'Circle Patio',
    enabled: true,
    // Under the gazebo (gazebo ≈ 0.30, 0.76) — soft pad, stretchable W/H.
    x: 0.3,
    y: 0.78,
    widthFrac: 0.34,
    heightFrac: 0.22,
    layouts: {
      desktop: {
        x: 0.3,
        y: 0.78,
        widthFrac: 0.34,
        heightFrac: 0.22,
      },
      tablet: {
        x: 0.3,
        y: 0.78,
        widthFrac: 0.32,
        heightFrac: 0.21,
      },
      mobile: {
        x: 0.28,
        y: 0.8,
        widthFrac: 0.3,
        heightFrac: 0.2,
      },
    },
    sprite: {
      src: `${SHARED_GROUND}/circle-patio-v1.png`,
      alt: 'Circular freestanding stone patio pad',
      imageWidth: 1200,
      imageHeight: 1200,
    },
    footprint: [],
  },
  {
    id: 'rounded-concrete-patio',
    title: 'Rounded Concrete Patio',
    enabled: true,
    // Mid-front lawn pad — independent of Hubert patio / circle patio.
    x: 0.58,
    y: 0.86,
    widthFrac: 0.3,
    heightFrac: 0.12,
    layouts: {
      desktop: {
        x: 0.58,
        y: 0.86,
        widthFrac: 0.3,
        heightFrac: 0.12,
        quadCorners: [
          { x: -0.15, y: -0.06 },
          { x: 0.15, y: -0.06 },
          { x: 0.15, y: 0.06 },
          { x: -0.15, y: 0.06 },
        ],
      },
      tablet: {
        x: 0.58,
        y: 0.86,
        widthFrac: 0.28,
        heightFrac: 0.115,
        quadCorners: [
          { x: -0.14, y: -0.0575 },
          { x: 0.14, y: -0.0575 },
          { x: 0.14, y: 0.0575 },
          { x: -0.14, y: 0.0575 },
        ],
      },
      mobile: {
        x: 0.56,
        y: 0.87,
        widthFrac: 0.26,
        heightFrac: 0.11,
        quadCorners: [
          { x: -0.13, y: -0.055 },
          { x: 0.13, y: -0.055 },
          { x: 0.13, y: 0.055 },
          { x: -0.13, y: 0.055 },
        ],
      },
    },
    procedural: ROUNDED_CONCRETE_PATIO_PROCEDURAL,
    quadCorners: [
      { x: -0.15, y: -0.06 },
      { x: 0.15, y: -0.06 },
      { x: 0.15, y: 0.06 },
      { x: -0.15, y: 0.06 },
    ],
    sprite: {
      src: `${SHARED_GROUND}/rounded-concrete-patio-v1.png`,
      alt: 'Rounded concrete backyard patio with control joints',
      imageWidth: 1200,
      imageHeight: 520,
    },
    footprint: [],
  },
];
